import moment from "moment";
import Image from "next/image";
import router from "next/router";
import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { PersistGate } from "redux-persist/integration/react";

import { getReceipt } from "../../../../../api/orders/getOrders";
import {
  OrderExtra,
  OrderFormula,
  OrderFormulaComponent,
  OrderResult,
} from "../../../../../api/types/order.type";
import { labelToAnchor } from "../../../../../api/utils/helpers/labelToAnchor";
import { translationsContent } from "../../../../../localization/translations";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectRestaurant } from "../../../../../redux/slices/restaurantSlice";
import { persistor } from "../../../../../redux/store";
import { useLanguage } from "../../../../../store/lang.context";
import { absoluteLoader } from "../../../../../utils/loader";
import { translate } from "../../../../../utils/translate";
import DarkButton from "../../../button/DarkButton";
import Button from "../../../button/SimpleButton";
import classes from "../orderCard.module.scss";

const OrderHistoryCard = (props: OrderResult) => {
  const [displayDetails, setDisplayDetails] = useState(false);
  const restaurant = useAppSelector(selectRestaurant);

  const chevronStyle = {
    transform: displayDetails ? "rotate(180deg)" : "",
    transition: "transform 150ms ease",
  };

  const { language } = useLanguage();

  const formatDate = (date: string) => {
    return `${moment(date).format("ll")}`;
  };

  const checkIfFormulaMainIsEmpty = (formula: OrderFormula) => {
    return (
      formula.bases.length == 0 &&
      formula.ingredients.length == 0 &&
      formula.sauces.length == 0
    );
  };

  const formatExtraComponent = (extra: OrderExtra) => {
    return ` ${extra.ingredient.title}${
      extra.quantity > 1 ? ` (${extra.quantity})` : ""
    }`;
  };

  const formatFormulaComponent = (component: OrderFormulaComponent) => {
    return ` ${component.ingredient.title}${
      component.quantity > 1 ? ` (${component.quantity})` : ""
    }`;
  };

  const displayFirstDetail = (formula: OrderFormula) => {
    return `${translate(translationsContent, "saladContent", language)} ${
      formula.bases.length > 0
        ? formula.bases.map((base) => formatFormulaComponent(base))
        : ""
    }${formula.bases.length > 0 && formula.ingredients.length > 0 ? "," : ""}${
      formula.ingredients.length > 0
        ? formula.ingredients.map((ing) => formatFormulaComponent(ing))
        : ""
    }${formula.sauces.length > 0 ? "," : ""} ${
      formula.sauces.length > 0
        ? formula.sauces.map((sauce) => formatFormulaComponent(sauce))
        : ""
    }`;
  };

  const displaySecondDetail = (formula: OrderFormula) => {
    return `${
      formula.supplements.length > 0
        ? formula.supplements.map((sup) => formatFormulaComponent(sup))
        : ""
    }`;
  };

  const downloadReceipt = async (orderId: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { success, data } = await getReceipt(orderId);
    if (success && data) {
      const binaryData = [data];
      const blob = new Blob(binaryData, { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_self";
      link.download = `eatsalad-${orderId}`;
      link.click();
      setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(url);
      }, 1000);
    } else {
      console.log("Error in downloading PDF");
    }
  };

  const handleSelectRestaurant = () => {
    router.push(`/carte/${labelToAnchor(restaurant.name)}/${restaurant.id}`);
  };

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div className={classes.orderContainer}>
        <div
          className={classes.orderDescription}
          onClick={() => setDisplayDetails(!displayDetails)}
        >
          <div className={classes.descriptionColomn}>
            <span className={classes.title}>
              {language === "nl" ? "TOTAAL" : "TOTAL"}
            </span>
            <span className={classes.description}>{props.price} €</span>
          </div>
          <div className={classes.descriptionColomn}>
            <span className={classes.title}>
              {translate(translationsContent, "orderDate", language)}
            </span>
            <span className={classes.description}>
              {formatDate(props.created_at)}
            </span>
          </div>
          <div className={classes.descriptionColomn}>
            <span className={classes.title}>
              {translate(translationsContent, "orderNumber", language)}
            </span>
            <span className={classes.description}>{props.trivec_number}</span>
          </div>
          <div className={classes.descriptionColomn}>
            <FaChevronDown style={chevronStyle} className={classes.chevron} />
          </div>
          <div
            className={`${classes.descriptionColomn} ${classes.descriptionDownloadBill}`}
          >
            <Button
              label={`${translate(
                translationsContent,
                "orderCardButton1",
                language
              )}`}
              onClick={() => {
                downloadReceipt(props.id);
              }}
            />
          </div>
        </div>
        <div
          className={`${classes.orderBody} ${
            displayDetails ? "" : classes.hide
          }`}
        >
          {!!props.formulas &&
            props.formulas.map((formula, i) => (
              <div key={i} className={classes.orderDetails}>
                <>
                  <div className={classes.orderImageContainer}>
                    <Image
                      loader={absoluteLoader}
                      src={formula.image}
                      alt={"Order image"}
                      width={85}
                      height={85}
                    />
                  </div>
                  <div
                    key={`order-history-formula-${i}`}
                    className={classes.descriptionContainer}
                  >
                    <h2 className={classes.descriptionTitle}>
                      {formula.title}
                    </h2>
                    {!checkIfFormulaMainIsEmpty(formula) ? (
                      <div
                        key={`detail-1-side-container-${i}`}
                        className={classes.detailContainer}
                      >
                        <span className={classes.checkmark}></span>
                        <span>{displayFirstDetail(formula)}</span>
                      </div>
                    ) : (
                      ""
                    )}
                    {displaySecondDetail(formula).length > 0 ? (
                      <div
                        key={`detail-2-side-container-${i}`}
                        className={classes.detailContainer}
                      >
                        <span className={classes.checkmark}></span>
                        <span>{displaySecondDetail(formula)}</span>
                      </div>
                    ) : (
                      ""
                    )}
                    {formula.sides.length > 0 &&
                      formula.sides.map((side, i) => (
                        <div
                          key={`side-container-${i}`}
                          className={classes.sideContainer}
                        >
                          <div className={classes.plus}>+</div>
                          <span>{formatFormulaComponent(side)}</span>
                        </div>
                      ))}
                  </div>
                </>
              </div>
            ))}

          {!!props.custom_salads &&
            props.custom_salads.map((formula, i) => (
              <div key={i} className={classes.orderDetails}>
                <>
                  <div className={classes.orderImageContainer}>
                    <Image
                      loader={absoluteLoader}
                      src={formula.image}
                      alt={"Order image"}
                      width={85}
                      height={85}
                    />
                  </div>
                  <div
                    key={`order-history-formula-${i}`}
                    className={classes.descriptionContainer}
                  >
                    <h2 className={classes.descriptionTitle}>
                      {formula.title}
                    </h2>
                    {!checkIfFormulaMainIsEmpty(formula) ? (
                      <div
                        key={`detail-1-side-container-${i}`}
                        className={classes.detailContainer}
                      >
                        <span className={classes.checkmark}></span>
                        <span>{displayFirstDetail(formula)}</span>
                      </div>
                    ) : (
                      ""
                    )}
                    {displaySecondDetail(formula).length > 0 ? (
                      <div
                        key={`detail-2-side-container-${i}`}
                        className={classes.detailContainer}
                      >
                        <span className={classes.checkmark}></span>
                        <span>{displaySecondDetail(formula)}</span>
                      </div>
                    ) : (
                      ""
                    )}
                    {formula.sides.length > 0 &&
                      formula.sides.map((side, i) => (
                        <div
                          key={`side-container-${i}`}
                          className={classes.sideContainer}
                        >
                          <div className={classes.plus}>+</div>
                          <span>{formatFormulaComponent(side)}</span>
                        </div>
                      ))}
                  </div>
                </>
              </div>
            ))}

          {!!props.extras &&
            props.extras.map((extra, i) => (
              <div key={i} className={classes.orderDetails}>
                <div className={classes.orderImageContainer}>
                  <Image
                    loader={absoluteLoader}
                    src={extra.ingredient.image}
                    alt={"Order image"}
                    width={85}
                    height={85}
                  />
                </div>
                <div
                  key={`order-history-extra-${i}`}
                  className={classes.descriptionContainer}
                >
                  <h2 className={classes.descriptionTitle}>
                    {formatExtraComponent(extra)}
                  </h2>
                </div>
              </div>
            ))}

          <div className={classes.placeButton}>
            <Button
              label={`${translate(
                translationsContent,
                "orderCardButton2",
                language
              )}`}
              onClick={() => {
                handleSelectRestaurant();
              }}
            />
          </div>
          <div className={classes.billButton}>
            <DarkButton
              label={`${translate(
                translationsContent,
                "orderCardButton1",
                language
              )}`}
              onClick={() => {
                downloadReceipt(props.id);
              }}
            />
          </div>
        </div>
      </div>
    </PersistGate>
  );
};

export default OrderHistoryCard;
